/**
 * The CRM's own settings, edited from the console's Settings → Styling tab.
 * The CRM is served from this origin, so both sides share one localStorage:
 * these are the CRM's own keys and value formats (crm/src/public/shared/ui.js,
 * crm-settings.js, chats/chat-transcript.js). The embedded CRM re-applies a
 * key when its `storage` event fires.
 *
 * The theme and the language are not here: they are app-wide (theme/themes.ts
 * writes `crm_theme`; the console language writes `crm_locale`, i18n/index.ts).
 */

export const CRM_LOCALES = ['ru', 'uk', 'en', 'es'] as const;
export type CrmLocale = (typeof CRM_LOCALES)[number];

export const CRM_TRANSLATE_TARGETS = ['en', 'ru', 'uk', 'fr', 'de', 'es', 'it', 'pt', 'pl', 'tr'] as const;
export type CrmTranslateTarget = (typeof CRM_TRANSLATE_TARGETS)[number];

export type CrmMediaView = 'expanded' | 'compact';

/** The page menu Fansly accounts show (crmRail MenuStyle); console-only, the CRM frame does not read it. */
export const FANSLY_MENU_STYLES = ['onlyfans', 'fansly'] as const;
export type FanslyMenuStyle = (typeof FANSLY_MENU_STYLES)[number];

/** How unread chats stand out in the chats list: bold plus a tinted row (default), or bold text only. Console-only. */
export const UNREAD_STYLES = ['bold', 'highlight'] as const;
export type UnreadStyle = (typeof UNREAD_STYLES)[number];

export const CRM_UI_SCALE = { min: 80, max: 150, step: 5 } as const;

export const CRM_STYLE_KEYS = {
  locale: 'crm_locale',
  composerTranslate: 'crm_composer_translate_target',
  legacyTranslate: 'crm_translate_target',
  messageTranslate: 'crm_message_translate_target',
  uiScale: 'crm_ui_scale',
  uiScaleDefault: 'crm_ui_scale_default',
  mediaView: 'crm_chat_media_view',
  fanslyMenu: 'crm_fansly_menu_style',
  unreadStyle: 'crm_unread_style',
  nyx: 'crm_pet_nyx_enabled',
  spentBadge: 'crm_spent_badge_enabled',
  buyingPower: 'crm_buying_power_enabled',
  mediaSalesStats: 'crm_media_sales_stats_enabled',
  messagePreviewTooltip: 'crm_message_preview_tooltip_enabled',
  shadeCreators: 'crm_shade_creators_enabled',
} as const;

/** The on/off settings: key and the CRM's default when nothing is saved. */
export const CRM_FLAGS = {
  nyx: { key: CRM_STYLE_KEYS.nyx, fallback: false },
  spentBadge: { key: CRM_STYLE_KEYS.spentBadge, fallback: true },
  buyingPower: { key: CRM_STYLE_KEYS.buyingPower, fallback: true },
  mediaSalesStats: { key: CRM_STYLE_KEYS.mediaSalesStats, fallback: false },
  messagePreviewTooltip: { key: CRM_STYLE_KEYS.messagePreviewTooltip, fallback: true },
  shadeCreators: { key: CRM_STYLE_KEYS.shadeCreators, fallback: true },
} as const;
export type CrmFlag = keyof typeof CRM_FLAGS;

export interface CrmStyling {
  /** The CRM's language: mirrors the console language (read-only here). */
  locale: CrmLocale;
  composerTranslate: CrmTranslateTarget;
  messageTranslate: CrmTranslateTarget;
  /** Default scale on: the CRM renders at 100% whatever `uiScale` says. */
  uiScaleDefault: boolean;
  uiScale: number;
  mediaView: CrmMediaView;
  fanslyMenu: FanslyMenuStyle;
  unreadStyle: UnreadStyle;
  flags: Record<CrmFlag, boolean>;
}

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function storage(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function read(store: Store | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function oneOf<T extends string>(list: readonly T[], value: string | null | undefined, fallback: T): T {
  const code = String(value ?? '').trim().toLowerCase();
  if (code === 'ua' && (list as readonly string[]).includes('uk')) return 'uk' as T;
  return (list as readonly string[]).includes(code) ? (code as T) : fallback;
}

export function normalizeUiScale(value: unknown): number {
  // Nothing saved means the default 100% (as the CRM reads it).
  const raw = value === null || value === undefined || value === '' ? 100 : Number(value);
  const finite = Number.isFinite(raw) ? raw : 100;
  const rounded = Math.round(finite / CRM_UI_SCALE.step) * CRM_UI_SCALE.step;
  return Math.max(CRM_UI_SCALE.min, Math.min(CRM_UI_SCALE.max, rounded));
}

/** Read every setting the way the embedded CRM would. */
export function readCrmStyling(store: Store | null = storage()): CrmStyling {
  const locale = oneOf(CRM_LOCALES, read(store, CRM_STYLE_KEYS.locale), 'ru');
  const savedDefault = read(store, CRM_STYLE_KEYS.uiScaleDefault);
  const savedScale = normalizeUiScale(read(store, CRM_STYLE_KEYS.uiScale));
  const uiScaleDefault = savedDefault === '1' ? true : savedDefault === '0' ? false : savedScale === 100;
  const flag = (f: CrmFlag) => {
    const v = read(store, CRM_FLAGS[f].key);
    return v === '1' ? true : v === '0' ? false : CRM_FLAGS[f].fallback;
  };
  return {
    locale,
    composerTranslate: oneOf(
      CRM_TRANSLATE_TARGETS,
      read(store, CRM_STYLE_KEYS.composerTranslate) || read(store, CRM_STYLE_KEYS.legacyTranslate),
      'en',
    ),
    messageTranslate: oneOf(CRM_TRANSLATE_TARGETS, read(store, CRM_STYLE_KEYS.messageTranslate) || locale, 'en'),
    uiScaleDefault,
    uiScale: savedScale,
    mediaView: read(store, CRM_STYLE_KEYS.mediaView) === 'compact' ? 'compact' : 'expanded',
    fanslyMenu: read(store, CRM_STYLE_KEYS.fanslyMenu) === 'fansly' ? 'fansly' : 'onlyfans',
    unreadStyle: read(store, CRM_STYLE_KEYS.unreadStyle) === 'bold' ? 'bold' : 'highlight',
    flags: {
      nyx: flag('nyx'),
      spentBadge: flag('spentBadge'),
      buyingPower: flag('buyingPower'),
      mediaSalesStats: flag('mediaSalesStats'),
      messagePreviewTooltip: flag('messagePreviewTooltip'),
      shadeCreators: flag('shadeCreators'),
    },
  };
}

/** Save one or more settings in the CRM's format. Returns false if storage is unavailable. */
export function writeCrmStyling(patch: Partial<Omit<CrmStyling, 'flags' | 'locale'>> & { flags?: Partial<Record<CrmFlag, boolean>> }, store: Store | null = storage()): boolean {
  if (!store) return false;
  const entries: [string, string][] = [];
  if (patch.composerTranslate) entries.push([CRM_STYLE_KEYS.composerTranslate, patch.composerTranslate]);
  if (patch.messageTranslate) entries.push([CRM_STYLE_KEYS.messageTranslate, patch.messageTranslate]);
  // Scale before its "default" switch: the CRM applies whichever event arrives,
  // and reads both keys each time.
  if (patch.uiScale !== undefined) entries.push([CRM_STYLE_KEYS.uiScale, String(normalizeUiScale(patch.uiScale))]);
  if (patch.uiScaleDefault !== undefined) entries.push([CRM_STYLE_KEYS.uiScaleDefault, patch.uiScaleDefault ? '1' : '0']);
  if (patch.mediaView) entries.push([CRM_STYLE_KEYS.mediaView, patch.mediaView]);
  if (patch.fanslyMenu) entries.push([CRM_STYLE_KEYS.fanslyMenu, patch.fanslyMenu]);
  if (patch.unreadStyle) entries.push([CRM_STYLE_KEYS.unreadStyle, patch.unreadStyle]);
  for (const [f, on] of Object.entries(patch.flags ?? {}) as [CrmFlag, boolean | undefined][]) {
    if (on !== undefined) entries.push([CRM_FLAGS[f].key, on ? '1' : '0']);
  }
  try {
    for (const [key, value] of entries) store.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** Keys this module reads, for listening to changes made elsewhere (the CRM itself, another tab). */
export const CRM_STYLE_KEY_SET: ReadonlySet<string> = new Set([
  ...Object.values(CRM_STYLE_KEYS),
]);

/** The CRM's language for a console language (they are the same three). */
export function crmLocaleOf(language: string): CrmLocale {
  return oneOf(CRM_LOCALES, language, 'en');
}

/** Mirror the console language to the CRM, so the frame speaks it too. */
export function writeCrmLocale(language: string, store: Store | null = storage()): boolean {
  if (!store) return false;
  const locale = crmLocaleOf(language);
  try {
    if (store.getItem(CRM_STYLE_KEYS.locale) !== locale) store.setItem(CRM_STYLE_KEYS.locale, locale);
    return true;
  } catch {
    return false;
  }
}
